/**
 * What a beat's published URLs will actually look like.
 *
 * SHOWN BEFORE SAVING, because the two URLs are the whole feature and they are
 * not guessable from a path field: one of them gains a date segment and the
 * other does not, and getting that wrong is only discovered by a reader
 * following a dead link.
 *
 * Pure and dependency-free so jest can reach it — the repo's CRA/router
 * constraint again.
 */

/** Trim blanks and slashes off each segment; drop the ones left empty.
 *
 *  A stray empty segment produces a double slash in the published path, which
 *  nobody notices until it 404s. The server does the same, so what is previewed
 *  here is what gets stored. */
export function cleanSegments(segments: string[]): string[] {
  return segments
    .map((s) => s.trim().replace(/^\/+|\/+$/g, '').trim())
    .filter(Boolean);
}

/** The stable URL: always the current briefing. */
export function latestPath(segments: string[]): string {
  const parts = cleanSegments(segments);
  return `/${parts.join('/')}${parts.length ? '/' : ''}`;
}

/**
 * The dated snapshot for one run.
 *
 * `sample` is only for the preview — the real one is the run's own date. It is
 * passed in rather than read from a clock so the preview is deterministic and
 * testable.
 */
export function archivePath(segments: string[], sample: string): string {
  const parts = cleanSegments(segments);
  return `/${[...parts, sample].join('/')}/`;
}

/** Is this beat configured enough to publish anything? */
export function canPublish(opts: {
  planId: number | null | undefined;
  segments: string[];
}): boolean {
  // A plan is required (it carries the site and is what the deploy runs on); a
  // path is not — an empty path publishes at the site root, which is unusual but
  // legitimate for a site that IS the briefing.
  return !!opts.planId;
}

/** Split a typed path into segments. Accepts "briefings/oil" or "/briefings/oil/". */
export function parsePath(raw: string): string[] {
  return cleanSegments(raw.split('/'));
}
